// Visual fixtures for hand-driving the widget's rendering states without
// running a real conversation.
//
// Each fixture is *data* — `state` describes what the widget's
// ConversationController should look like after a hypothetical turn
// (messages, cart, hint, fulfillment, log). `<bb-widget fixture-id="…">`
// seeds itself from this module on mount and skips every API call.
//
// The same data is consumed by three host pages:
//   - `widget/fixtures.html`           — bare widget dev harness
//   - `demos/sales/public/fixtures.html`   — sales chrome + widget
//   - `demos/dusan/public/fixtures.html`   — dusan chrome + widget

import type { ChatMessage } from '../bbcore/conversation'
import type { LogEntry } from '../bbcore/log'
import type {
  Cart,
  ExtrasChoiceHint,
  FulfillmentInputHint,
  Hint,
  ItemsSuggestionHint,
  ModifierChoiceHint,
  QuantityConfirmHint,
  ScheduledTimeConfirmHint,
  SubmitConfirmHint,
} from '../bbcore/types'

export interface FixtureState {
  messages?: ChatMessage[]
  cart?: Cart | null
  hint?: Hint | null
  fulfillmentType?: string | null
  logEntries?: LogEntry[]
  conversationState?: string | null
  orderId?: string | null
  // Set true to render the submitting-overlay (a fixture for the
  // blocking submit-in-flight UI).
  submitting?: boolean
}

export interface Fixture {
  id: string
  label: string
  description?: string
  state: FixtureState
  // Optional widget `panels` attribute override (host pages may use their
  // own default). Example: 'menu,chat,cart,log'.
  panels?: string
  // Optional override of bb-widget's `fulfillment-render` attribute.
  // 'inline' (default) renders fulfillment hints in the chat transcript;
  // 'overlay' renders them as a visually-modal card over the widget.
  fulfillmentRender?: 'inline' | 'overlay'
  // Optional override of bb-widget's `thankyou-render` attribute.
  thankyouRender?: 'panel' | 'modal'
  // Optional override of bb-widget's `submit-render` attribute.
  submitRender?: 'inline' | 'overlay'
}

// ── helpers ─────────────────────────────────────────────────────────

let _seqId = 1
const _nextId = () => _seqId++

// Build the canonical log entries you'd see in BerryBot's real SSE
// stream for a "user said X, bot replied with hint Y" turn — one
// POST .../utterances + one hint event. Lets fixtures stay readable.
export function fakeTurnLog(opts: {
  conversationId?: string
  utterance: string
  reply: string
  hint?: Hint
  ms?: number
}): LogEntry[] {
  const at = Date.now()
  const cid = opts.conversationId ?? 'conv_fixture'
  const ms = opts.ms ?? 1800
  const entries: LogEntry[] = [
    {
      kind: 'http',
      id: _nextId(),
      at,
      method: 'POST',
      path: `/conversations/${cid}/utterances`,
      status: 200,
      ms,
      ok: true,
      requestBody: JSON.stringify({ text: opts.utterance }),
      responseBody: JSON.stringify({ message: opts.reply, hint: opts.hint }),
    },
  ]
  if (opts.hint) {
    entries.push({ kind: 'hint', id: _nextId(), at: at + 1, hint: opts.hint })
  }
  return entries
}

// ── fixtures ────────────────────────────────────────────────────────

const fulfillmentPickupOrDelivery: FulfillmentInputHint = {
  type: 'input_needed',
  subject: 'fulfillment_type',
  version: 1,
  id: 'fixture:fulfillment_type:1',
  options: ['pick_up', 'delivery'],
}

export const FIXTURES: Fixture[] = [
  {
    id: 'fulfillment-pickup-delivery',
    label: 'Fulfillment — Pickup / Delivery',
    description:
      'Bot offers both Pickup and Delivery via an `input_needed:fulfillment_type` hint. Delivery expands inline to an address field.',
    state: {
      messages: [
        { who: 'bot', text: 'Hi! What would you like to order?' },
        { who: 'user', text: "I'd like a salmon poke bowl." },
        {
          who: 'bot',
          text:
            "Got it — one salmon poke bowl. Will you be picking up or " +
            'should we deliver?',
          hint: fulfillmentPickupOrDelivery,
        },
      ],
      hint: fulfillmentPickupOrDelivery,
      logEntries: fakeTurnLog({
        utterance: "I'd like a salmon poke bowl.",
        reply:
          'Got it — one salmon poke bowl. Will you be picking up or ' +
          'should we deliver?',
        hint: fulfillmentPickupOrDelivery,
      }),
    },
  },
  {
    id: 'fulfillment-overlay',
    label: 'Fulfillment — overlay popup',
    description:
      'Same hint as above, rendered as a visually-modal card centered over the widget instead of inline in the chat. Chat panel suppresses its own inline render.',
    fulfillmentRender: 'overlay',
    state: {
      messages: [
        { who: 'bot', text: 'Hi! What would you like to order?' },
        { who: 'user', text: "I'd like a salmon poke bowl." },
        {
          who: 'bot',
          text:
            "Got it — one salmon poke bowl. Will you be picking up or " +
            'should we deliver?',
          hint: fulfillmentPickupOrDelivery,
        },
      ],
      hint: fulfillmentPickupOrDelivery,
      logEntries: fakeTurnLog({
        utterance: "I'd like a salmon poke bowl.",
        reply:
          'Got it — one salmon poke bowl. Will you be picking up or ' +
          'should we deliver?',
        hint: fulfillmentPickupOrDelivery,
      }),
    },
  },
]

FIXTURES.push({
  id: 'submitting-overlay',
  label: 'Submitting overlay',
  description:
    'Blocking spinner over the widget while a submit is in flight. Same overlay that appears during a real submitOrder() POST.',
  state: {
    messages: [
      { who: 'bot', text: 'Hi! What would you like to order?' },
      { who: 'user', text: 'Salmon poke bowl, pickup please.' },
      { who: 'bot', text: 'Placing your order now.' },
    ],
    submitting: true,
  },
})

FIXTURES.push({
  id: 'submit-confirm',
  label: 'Submit-order confirm — overlay',
  description:
    'Order-confirm popup rendered as a centered modal: line-item recap + fulfillment summary + Place my order CTA + dismiss. Pairs with fulfillment overlay as the follow-up step.',
  submitRender: 'overlay',
  state: {
    messages: [
      { who: 'bot', text: 'Hi! What would you like to order?' },
      { who: 'user', text: "I'd like a salmon poke bowl." },
      { who: 'bot', text: "Sure — pickup or delivery?" },
      { who: 'user', text: "Pickup." },
      {
        who: 'bot',
        text: "Got it — pickup. Ready to place the order?",
        hint: {
          type: 'confirmation_needed',
          subject: 'submit_order',
          version: 1,
          id: 'fixture:submit:1',
          context: { items_count: 1, fulfillment_type: 'pick_up' },
        },
      },
    ],
    cart: {
      items: [
        {
          item: 'Salmon Poke Bowl',
          quantity: 1,
          modifiers: [],
          name: 'Salmon Poke Bowl',
          price: 'R154.00',
        } as unknown as import('../bbcore/types').CartItem,
      ],
    },
    fulfillmentType: 'pick_up',
    hint: {
      type: 'confirmation_needed',
      subject: 'submit_order',
      version: 1,
      id: 'fixture:submit:1',
      context: { items_count: 1, fulfillment_type: 'pick_up' },
    },
  },
})

FIXTURES.push({
  id: 'thankyou-modal',
  label: 'Thank-you — modal',
  description:
    'Order-complete confirmation rendered as a centered modal over the (frozen) chat panel instead of replacing it.',
  thankyouRender: 'modal',
  state: {
    messages: [
      { who: 'bot', text: 'Hi! What would you like to order?' },
      { who: 'user', text: "I'd like a salmon poke bowl, pickup." },
      { who: 'bot', text: 'All set. Submitting your order…' },
    ],
    conversationState: 'complete',
    orderId: 'BB-90421',
  },
})

// ── inline hint-chip fixtures ───────────────────────────────────────
// These render the hint affordances *inline in the chat transcript*
// (the default `fulfillment-render`/`submit-render` = inline, render-hints
// on), as opposed to the overlay/modal fixtures above. The chat panel
// renders the affordance below the last bot bubble straight from
// `state.hint` — no network. One fixture per hint kind bb-chat renders.

const submitInlineHint: SubmitConfirmHint = {
  type: 'confirmation_needed',
  subject: 'submit_order',
  version: 1,
  id: 'fixture:submit-inline:1',
  context: { items_count: 1, fulfillment_type: 'pick_up' },
}

FIXTURES.push({
  id: 'submit-inline',
  label: 'Submit — inline chip',
  description:
    'The `confirmation_needed:submit_order` hint rendered inline in the chat as a single "Place my order" affordance (vs. the overlay submit-confirm card).',
  state: {
    messages: [
      { who: 'bot', text: 'Hi! What would you like to order?' },
      { who: 'user', text: "I'd like a salmon poke bowl." },
      { who: 'bot', text: 'Sure — pickup or delivery?' },
      { who: 'user', text: 'Pickup.' },
      {
        who: 'bot',
        text: 'Got it — pickup. Ready to place the order?',
        hint: submitInlineHint,
      },
    ],
    cart: {
      items: [
        {
          item: 'Salmon Poke Bowl',
          quantity: 1,
          modifiers: [],
          name: 'Salmon Poke Bowl',
          price: 'R154.00',
        } as unknown as import('../bbcore/types').CartItem,
      ],
    },
    fulfillmentType: 'pick_up',
    hint: submitInlineHint,
  },
})

const quantityConfirmHint: QuantityConfirmHint = {
  type: 'confirmation_needed',
  subject: 'add_quantity',
  version: 1,
  id: 'fixture:quantity:1',
  options: ['yes', 'no'],
  context: { item_name: 'Salmon Poke Bowl', quantity: 6, reason: 'quantity_threshold' },
}

FIXTURES.push({
  id: 'quantity-confirm',
  label: 'Quantity confirm — Yes / No',
  description:
    'Blocking `confirmation_needed:add_quantity` hint (quantity > 4) — inline Yes / No chips. Yes retries add_to_cart with confirmed=True; No drops the pending add.',
  state: {
    messages: [
      { who: 'bot', text: 'Hi! What would you like to order?' },
      { who: 'user', text: 'Six salmon poke bowls please.' },
      {
        who: 'bot',
        text: "That's 6 Salmon Poke Bowls — quite a lot! Want me to add all six?",
        hint: quantityConfirmHint,
      },
    ],
    hint: quantityConfirmHint,
  },
})

const scheduledTimeHint: ScheduledTimeConfirmHint = {
  type: 'confirmation_needed',
  subject: 'scheduled_time',
  version: 1,
  id: 'fixture:scheduled:1',
  context: {
    suggested_iso: '2026-06-14T11:00',
    suggested_label: 'tomorrow at 11:00 AM',
    earliest_iso: '2026-06-14T11:00',
    store_time_zone: 'Africa/Johannesburg',
  },
}

FIXTURES.push({
  id: 'scheduled-time',
  label: 'Scheduled time — slot picker',
  description:
    'Blocking `confirmation_needed:scheduled_time` hint (store closed): accept the suggested slot, pick another time, or decline. All controls send plain utterances.',
  state: {
    messages: [
      { who: 'bot', text: 'Hi! What would you like to order?' },
      { who: 'user', text: 'A salmon poke bowl for pickup.' },
      {
        who: 'bot',
        text: "We're closed right now. The earliest we can have it ready is tomorrow at 11:00 AM — does that work?",
        hint: scheduledTimeHint,
      },
    ],
    hint: scheduledTimeHint,
  },
})

const modifiersHint: ModifierChoiceHint = {
  type: 'choice_needed',
  subject: 'modifiers',
  version: 1,
  id: 'fixture:modifiers:1',
  context: {
    item_id: 'item_poke',
    item_name: 'Build-Your-Own Poke Bowl',
    price_cents: 15400,
    quantity: 1,
  },
  groups: [
    {
      name: 'Base',
      min_selections: 1,
      max_selections: 1,
      options: [
        { name: 'White Rice', price_cents: 0 },
        { name: 'Brown Rice', price_cents: 0 },
        { name: 'Mixed Greens', price_cents: 0 },
      ],
    },
    {
      name: 'Protein',
      min_selections: 1,
      max_selections: 2,
      options: [
        { name: 'Salmon', price_cents: 0 },
        { name: 'Tuna', price_cents: 2000 },
        { name: 'Tofu', price_cents: 0 },
      ],
    },
  ],
}

FIXTURES.push({
  id: 'modifiers-picker',
  label: 'Modifiers — required picker',
  description:
    'Blocking `choice_needed:modifiers` hint — the inline modifier picker (min/max selections per group) rendered by bb-chat-modifier.',
  state: {
    messages: [
      { who: 'bot', text: 'Hi! What would you like to order?' },
      { who: 'user', text: 'A build-your-own poke bowl.' },
      {
        who: 'bot',
        text: 'Great — let’s build your poke bowl. Pick a base and a protein:',
        hint: modifiersHint,
      },
    ],
    hint: modifiersHint,
  },
})

const extrasHint: ExtrasChoiceHint = {
  type: 'choice_needed',
  subject: 'extras',
  version: 1,
  id: 'fixture:extras:1',
  context: {
    item_id: 'item_poke',
    item_name: 'Salmon Poke Bowl',
    quantity: 1,
  },
  groups: [
    {
      name: 'Add-ons',
      min_selections: 0,
      max_selections: 5,
      options: [
        { name: 'Avocado', price_cents: 2500 },
        { name: 'Edamame', price_cents: 1800 },
        { name: 'Seaweed Salad', price_cents: 2200 },
      ],
    },
  ],
}

FIXTURES.push({
  id: 'extras-chips',
  label: 'Extras — upsell chips',
  description:
    'Non-blocking `choice_needed:extras` hint — optional add-on chips for the just-added item. Sticky; clicking each sends a plain "add X to my Y" utterance.',
  state: {
    messages: [
      { who: 'bot', text: 'Hi! What would you like to order?' },
      { who: 'user', text: 'A salmon poke bowl.' },
      {
        who: 'bot',
        text: 'Added a Salmon Poke Bowl. Want to add any extras?',
        hint: extrasHint,
      },
    ],
    cart: {
      items: [
        {
          item: 'Salmon Poke Bowl',
          quantity: 1,
          modifiers: [],
          name: 'Salmon Poke Bowl',
          price: 'R154.00',
        } as unknown as import('../bbcore/types').CartItem,
      ],
    },
    hint: extrasHint,
  },
})

const suggestionsHint: ItemsSuggestionHint = {
  type: 'suggestions',
  subject: 'items',
  version: 1,
  id: 'fixture:suggestions:1',
  items: [
    { name: 'Salmon Poke Bowl', price_cents: 15400 },
    { name: 'Spicy Tuna Roll', price_cents: 9800 },
    { name: 'Chicken Katsu', price_cents: 12500 },
    { name: 'Miso Soup', price_cents: 4500 },
  ],
}

FIXTURES.push({
  id: 'suggestions-chips',
  label: 'Suggestions — item chips',
  description:
    'Non-blocking `suggestions:items` hint — search/recommendation chips anchored to the bot turn they arrived with. Clicking a chip sends "I’ll have the X".',
  state: {
    messages: [
      { who: 'bot', text: 'Hi! What would you like to order?' },
      { who: 'user', text: "What's popular?" },
      {
        who: 'bot',
        text: 'Here are a few favourites:',
        hint: suggestionsHint,
      },
    ],
    hint: suggestionsHint,
  },
})

export function findFixture(id: string | null): Fixture | undefined {
  if (!id) return undefined
  return FIXTURES.find((f) => f.id === id)
}
